import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Spinner, Badge } from 'react-bootstrap';
import {
  FaFileAlt,
  FaExternalLinkAlt,
  FaDownload,
  FaFilePdf,
  FaPlus,
  FaTimes,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import './styles.css';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';

const ModalDocumentos = ({
  show,
  onHide,
  modalType,
  empreendimentoNome,
  codigoGrupoEmpresa,
}) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const fileInputRef = useRef(null);

  const getCategoria = () => {
    switch (modalType) {
      case 'IMPLANTAÇÃO':
        return 'implantacao';
      case 'COMUNICADOS':
        return 'documentos';
      default:
        return null;
    }
  };

  useEffect(() => {
    if (show && codigoGrupoEmpresa) {
      carregarDocumentos();
    }
  }, [show, codigoGrupoEmpresa, modalType]);

  const carregarDocumentos = async () => {
    const categoria = getCategoria();
    if (!categoria) return;

    setLoading(true);
    try {
      const data = await api.SismacListar(categoria, codigoGrupoEmpresa);
      setDocumentos(data.arquivos || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error(
        (error && error.message) ||
          'Erro ao carregar documentos. Tente novamente.'
      );
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizarDocumento = async doc => {
    const categoria = getCategoria();
    if (!categoria) return;

    try {
      toast.success(`Abrindo documento: ${doc.nome}`);
      setDownloading({ [doc.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        doc.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(function() {
        window.URL.revokeObjectURL(url);
        setDownloading(prev => ({ ...prev, [doc.nome_completo]: false }));
      }, 100);
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast.error('Erro ao visualizar documento.');
      setDownloading(prev => ({ ...prev, [doc.nome_completo]: false }));
    }
  };

  const handleDownloadDocumento = async doc => {
    const categoria = getCategoria();
    if (!categoria) return;

    try {
      toast.success(`Download iniciado: ${doc.nome}`);
      setDownloading({ [doc.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        doc.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download concluído!');
      setDownloading({ [doc.nome_completo]: false });
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do documento.');
      setDownloading(prev => ({ ...prev, [doc.nome_completo]: false }));
    }
  };

  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

 const handleUploadDocumento = async event => {
   const categoria = getCategoria();
   if (!categoria) return;

   const files = event.target.files;
   if (!files || files.length === 0) return;

   setUploading(true);

   try {
     for (let i = 0; i < files.length; i++) {
       const file = files[i];

       if (!file.name.toLowerCase().endsWith('.pdf')) {
         toast.error('Apenas arquivos PDF são permitidos!');
         continue;
       }

       const formData = new FormData();
       formData.append('file', file);

       await api.SismacUploadArquivo(categoria, codigoGrupoEmpresa, formData);

       toast.success(file.name + ' enviado com sucesso!');
     }

     await carregarDocumentos();
   } catch (error) {
     console.error('Erro ao fazer upload:', error);
     toast.error('Erro ao fazer upload do documento.');
   } finally {
     setUploading(false);
     if (fileInputRef.current) {
       fileInputRef.current.value = '';
     }
   }
 };

  const handleExcluirDocumento = async doc => {
    const categoria = getCategoria();
    if (!categoria) return;

    try {
      await api.SismacExcluirArquivo(
        categoria,
        codigoGrupoEmpresa,
        doc.nome_completo
      );
      toast.success('Documento excluído com sucesso!');
      await carregarDocumentos();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento.');
    }
  };

  const handleClose = () => {
    setDocumentos([]);
    onHide();
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="sismac_modal"
    >
      <Modal.Header closeButton >
        <Modal.Title style={{ color: '#fff' }}>
          {modalType} - {empreendimentoNome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: '#0747a6' }} />
            <p className="mt-2" style={{ color: '#0747a6' }}>
              Carregando documentos...
            </p>
          </div>
        ) : (
          <Card className="sismac_card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center text-secondary">
                <FaFilePdf className="mr-2" />
                <span>Documentos</span>
                {documentos.length > 0 && (
                  <Badge bg="secondary" className="ms-2">
                    {documentos.length}
                  </Badge>
                )}
              </h6>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf"
                  multiple
                  onChange={handleUploadDocumento}
                />
                {AuthStorage.temCaracteristica(caracteristicas.GerencialSismac) && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleFileInputClick}
                    disabled={uploading}
                  >
                    <FaPlus className="mr-1" />
                    {uploading ? 'Enviando...' : 'Adicionar arquivo'}
                  </Button>
                )}
              </div>
            </Card.Header>

            <Card.Body>
              {documentos.length === 0 ? (
                <div className="sismac_empty">
                  <FaFileAlt size={40} className="sismac_empty-icon" />
                  <p className="sismac_empty-text">
                    Nenhum documento encontrado.
                  </p>
                </div>
              ) : (
                <div className="sismac_list">
                  {documentos.map((doc, index) => (
                    <div key={index} className="sismac_list-item">
                      <div className="sismac_list-item-content">
                        <FaFilePdf
                          className="sismac_list-item-icon sismac_list-item-icon--danger"
                          size={24}
                        />
                        <div className="sismac_list-item-info">
                          <span
                            className="sismac_list-item-title"
                            onClick={() => handleVisualizarDocumento(doc)}
                          >
                            <FaExternalLinkAlt size={12} />
                            {doc.nome}
                          </span>
                          <div className="sismac_list-item-subtitle">
                            {doc.tamanho_formatado} • {doc.extensao}
                          </div>
                        </div>
                      </div>
                      <div className="sismac_list-item-actions">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleDownloadDocumento(doc)}
                          disabled={downloading[doc.nome_completo]}
                        >
                          <FaDownload className="mr-1" />
                          {downloading[doc.nome_completo] ? 'Baixando...' : 'Download'}
                        </Button>
                        {AuthStorage.temCaracteristica(caracteristicas.GerencialSismac) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleExcluirDocumento(doc)}
                          >
                            <FaTimes className="mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDocumentos;
